/**
 * GestureRecognizer - Sistema de reconocimiento de gestos médicos
 * Interpreta landmarks de MediaPipe como gestos clínicos específicos
 */

import * as THREE from 'three';
import { HandState } from '../core/state';

// ═══════════════════════════════════════════════════════════════════════════════
// TIPOS DE GESTOS MÉDICOS
// ═══════════════════════════════════════════════════════════════════════════════

export enum MedicalGesture {
  // Gestos básicos de exploración
  PALPATION = 'palpation',              // 👌 Pinch sostenido = examinar nodo
  AUSCULTATION = 'auscultation',        // ✋ Mano abierta cerca = "escuchar"
  PERCUSSION = 'percussion',            // 👆 Pinch rápido = testear
  
  // Gestos complejos
  TIME_DILATION = 'time_dilation',      // 🙌 Two hands separar/juntar = control tiempo
  ANATOMICAL_CUT = 'anatomical_cut',    // ✋ Mano cuchillo = cambiar plano
  DIFFERENTIAL_PULL = 'differential_pull', // ↕️ Arrastrar a diagnóstico
  EMERGENCY_CLEAR = 'emergency_clear',  // 🙆 Ambas manos arriba = modo ABC
  
  // Navegación
  LAYER_SWITCH = 'layer_switch',        // 🔄 Rotar muñeca = cambiar capa
  ZOOM_FOCUS = 'zoom_focus',            // 🔍 Two hands pinch = zoom
  TEMPORAL_SCROLL = 'temporal_scroll',  // ⏮️ Swipe horizontal = tiempo
  
  // Manipulación
  GRAB = 'grab',                        // ✊ Cerrar puño = agarrar
  RELEASE = 'release',                  // 🖐️ Abrir mano = soltar
  PUSH = 'push',                        // 👋 Empujar = mover
  
  // Ningún gesto detectado
  NONE = 'none'
}

// ═══════════════════════════════════════════════════════════════════════════════
// CONFIGURACIÓN
// ═══════════════════════════════════════════════════════════════════════════════

interface GestureConfig {
  pinchThreshold: number;              // Distancia para considerar pinch
  grabThreshold: number;               // Distancia para considerar grab
  swipeVelocityThreshold: number;      // Velocidad mínima para swipe
  holdDuration: number;                // ms para considerar hold
  gestureCooldown: number;             // ms entre gestos
}

const DEFAULT_CONFIG: GestureConfig = {
  pinchThreshold: 0.08,  // palpación/percusión: más estricto que el click DOM para evitar falsos positivos
  grabThreshold: 0.08,
  swipeVelocityThreshold: 0.5,
  holdDuration: 500,
  gestureCooldown: 300,
};

// ═══════════════════════════════════════════════════════════════════════════════
// CLASE PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════════

export class GestureRecognizer {
  private config: GestureConfig;
  
  // Estado de seguimiento
  private gestureHistory: { gesture: MedicalGesture; time: number }[] = [];
  private lastGestureTime: number = 0;
  private pinchStartTime: number = 0;
  private isPinching: boolean = false;

  // Hold tracking: gestos que requieren mantener una postura N ms antes de
  // disparar, para evitar falsos positivos por posturas naturales.
  private auscultationHoldStart = 0;
  private emergencyHoldStart = 0;
  /**
   * "Lock" post-fire: al disparar un gesto, bloqueamos el re-disparo hasta
   * que el alumno rompa la postura (baje la mano, la cierre, la mueva).
   * Sin esto, mantener la postura provoca disparos en loop cada AUSCULTATION_HOLD_MS
   * y el HUD parpadea constante.
   */
  private auscultationLocked = false;
  private emergencyLocked = false;
  private static readonly AUSCULTATION_HOLD_MS = 1500;
  private static readonly EMERGENCY_HOLD_MS = 2000;

  /**
   * Contexto opcional inyectado por GestureHandler en cada tick. Permite que
   * AUSCULTATION sólo arranque si la mano está sobre un nodo del grafo:
   * si no hay target, auscultar no tiene efecto y mostrar el HUD molesta.
   */
  hasHoverTarget = false;
  
  // Landmark indices para MediaPipe
  private static readonly WRIST = 0;
  private static readonly THUMB_TIP = 4;
  private static readonly INDEX_TIP = 8;
  private static readonly MIDDLE_TIP = 12;
  private static readonly RING_TIP = 16;
  private static readonly PINKY_TIP = 20;

  constructor(config?: Partial<GestureConfig>) {
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // ═════════════════════════════════════════════════════════════════════════════
  // RECONOCIMIENTO PRINCIPAL
  // ═════════════════════════════════════════════════════════════════════════════
  
  recognize(
    handState: HandState,
    previousState?: HandState,
    otherHand?: HandState
  ): { gesture: MedicalGesture; confidence: number; data?: Record<string, unknown> } {
    const now = Date.now();
    
    // Cooldown entre gestos
    if (now - this.lastGestureTime < this.config.gestureCooldown) {
      return { gesture: MedicalGesture.NONE, confidence: 0 };
    }

    if (!handState.isPresent || !handState.landmarks) {
      return { gesture: MedicalGesture.NONE, confidence: 0 };
    }

    const landmarks = handState.landmarks;
    
    // Calcular métricas
    const pinchDistance = this.calculatePinchDistance(landmarks);
    const grabStrength = this.calculateGrabStrength(landmarks);
    const handOpenness = this.calculateHandOpenness(landmarks);
    const velocity = previousState 
      ? handState.position.distanceTo(previousState.position) / 0.016 // asumiendo 60fps
      : 0;
    
    // Detectar gestos individuales
    let result: { gesture: MedicalGesture; confidence: number; data?: Record<string, unknown> } = 
      { gesture: MedicalGesture.NONE, confidence: 0 };

    // 1. Detectar pinch (para palpación)
    if (pinchDistance < this.config.pinchThreshold) {
      if (!this.isPinching) {
        this.isPinching = true;
        this.pinchStartTime = now;
      }
      
      // Pinch sostenido = Palpación
      const pinchDuration = now - this.pinchStartTime;
      if (pinchDuration > this.config.holdDuration) {
        result = { 
          gesture: MedicalGesture.PALPATION, 
          confidence: Math.min(pinchDuration / 1000, 1),
          data: { duration: pinchDuration }
        };
      }
    } else {
      if (this.isPinching) {
        // Se soltó el pinch
        this.isPinching = false;
        
        // Pinch rápido = Percusión
        if (now - this.pinchStartTime < 200) {
          result = { 
            gesture: MedicalGesture.PERCUSSION, 
            confidence: 0.9 
          };
        }
      }
    }

    // 2. Detectar agarre
    if (grabStrength > 0.8) {
      result = { 
        gesture: MedicalGesture.GRAB, 
        confidence: grabStrength,
        data: { strength: grabStrength }
      };
    } else if (previousState?.isGrabbing && grabStrength < 0.3) {
      result = { 
        gesture: MedicalGesture.RELEASE, 
        confidence: 0.9 
      };
    }

    // 3. Mano EXTREMADAMENTE abierta (≥0.98, dedos totalmente estirados) y
    // muy quieta (<0.04), en altura normal (y<1.5), SOBRE un nodo, sostenida
    // ≥1.5s → Auscultación. Thresholds conservadores para que una mano
    // relajada no dispare: exige intención clara. Lock post-fire obliga a
    // romper la postura antes de volver a medir.
    const ausculturePose = handOpenness > 0.98
      && velocity < 0.04
      && handState.position.y < 1.5
      && this.hasHoverTarget;

    if (ausculturePose && !this.auscultationLocked) {
      if (this.auscultationHoldStart === 0) {
        this.auscultationHoldStart = now;
      } else if (now - this.auscultationHoldStart >= GestureRecognizer.AUSCULTATION_HOLD_MS) {
        result = {
          gesture: MedicalGesture.AUSCULTATION,
          confidence: handOpenness,
        };
        this.auscultationHoldStart = 0;
        this.auscultationLocked = true;
      }
    } else if (!ausculturePose) {
      // Postura rota: reset timer + lock (puede volver a intentar).
      this.auscultationHoldStart = 0;
      this.auscultationLocked = false;
    }

    // 4. Swipe horizontal = Scroll temporal
    if (velocity > this.config.swipeVelocityThreshold) {
      const direction = previousState 
        ? handState.position.clone().sub(previousState.position)
        : new THREE.Vector3();
      
      if (Math.abs(direction.x) > Math.abs(direction.y)) {
        result = {
          gesture: MedicalGesture.TEMPORAL_SCROLL,
          confidence: Math.min(velocity, 1),
          data: { direction: direction.x > 0 ? 'forward' : 'backward', velocity }
        };
      }
    }

    // 5. Mano MUY arriba (y>4, brazo bien extendido) + abierta (>0.92) =
    // Emergencia. Toggle destructivo, exige postura explícita sostenida ≥2s.
    // Subimos y de 3→4 y openness de 0.7→0.92 para que un saludo o señalar
    // al techo no active modo emergencia.
    const emergencyPose = handState.position.y > 4 && handOpenness > 0.92;

    if (emergencyPose && !this.emergencyLocked) {
      if (this.emergencyHoldStart === 0) {
        this.emergencyHoldStart = now;
      } else if (now - this.emergencyHoldStart >= GestureRecognizer.EMERGENCY_HOLD_MS) {
        result = {
          gesture: MedicalGesture.EMERGENCY_CLEAR,
          confidence: 0.9,
          data: { height: handState.position.y, heldMs: now - this.emergencyHoldStart },
        };
        this.emergencyHoldStart = 0;
        this.emergencyLocked = true;
      }
    } else if (!emergencyPose) {
      this.emergencyHoldStart = 0;
      this.emergencyLocked = false;
    }

    // 6. Gestos de dos manos
    if (otherHand?.isPresent && otherHand.landmarks) {
      const twoHandResult = this.recognizeTwoHandGesture(
        handState, otherHand, landmarks, otherHand.landmarks
      );
      
      if (twoHandResult.confidence > result.confidence) {
        result = twoHandResult;
      }
    }

    // Actualizar historial
    if (result.gesture !== MedicalGesture.NONE) {
      this.gestureHistory.push({ gesture: result.gesture, time: now });
      if (this.gestureHistory.length > 10) {
        this.gestureHistory.shift();
      }
      this.lastGestureTime = now;
    }

    return result;
  }

  // ═════════════════════════════════════════════════════════════════════════════
  // GESTOS DE DOS MANOS
  // ═════════════════════════════════════════════════════════════════════════════
  
  private recognizeTwoHandGesture(
    hand1: HandState,
    hand2: HandState,
    landmarks1: Array<{x: number, y: number, z: number}>,
    landmarks2: Array<{x: number, y: number, z: number}>
  ): { gesture: MedicalGesture; confidence: number; data?: Record<string, unknown> } {
    const distance = hand1.position.distanceTo(hand2.position);
    const center = hand1.position.clone().add(hand2.position).multiplyScalar(0.5);
    
    // Time dilation: dos manos separándose/juntándose
    const pinch1 = this.calculatePinchDistance(landmarks1);
    const pinch2 = this.calculatePinchDistance(landmarks2);
    
    if (pinch1 < this.config.pinchThreshold && pinch2 < this.config.pinchThreshold) {
      return {
        gesture: MedicalGesture.ZOOM_FOCUS,
        confidence: 0.85,
        data: { distance, center }
      };
    }
    
    // Dos manos arriba = emergencia
    if (hand1.position.y > 3 && hand2.position.y > 3) {
      return {
        gesture: MedicalGesture.EMERGENCY_CLEAR,
        confidence: 0.95,
        data: { emergencyLevel: 'critical' }
      };
    }
    
    return { gesture: MedicalGesture.NONE, confidence: 0 };
  }

  // ═════════════════════════════════════════════════════════════════════════════
  // CÁLCULOS DE LANDMARKS
  // ═════════════════════════════════════════════════════════════════════════════
  
  private calculatePinchDistance(landmarks: Array<{x: number, y: number, z: number}>): number {
    const thumb = landmarks[GestureRecognizer.THUMB_TIP];
    const index = landmarks[GestureRecognizer.INDEX_TIP];
    
    const dx = thumb.x - index.x;
    const dy = thumb.y - index.y;
    const dz = thumb.z - index.z;
    
    return Math.sqrt(dx * dx + dy * dy + dz * dz);
  }

  private calculateGrabStrength(landmarks: Array<{x: number, y: number, z: number}>): number {
    // Calcular qué tan cerrada está la mano
    const wrist = landmarks[GestureRecognizer.WRIST];
    const tips = [
      landmarks[GestureRecognizer.THUMB_TIP],
      landmarks[GestureRecognizer.INDEX_TIP],
      landmarks[GestureRecognizer.MIDDLE_TIP],
      landmarks[GestureRecognizer.RING_TIP],
      landmarks[GestureRecognizer.PINKY_TIP],
    ];
    
    let totalDistance = 0;
    tips.forEach(tip => {
      const dx = tip.x - wrist.x;
      const dy = tip.y - wrist.y;
      const dz = tip.z - wrist.z;
      totalDistance += Math.sqrt(dx * dx + dy * dy + dz * dz);
    });
    
    const avgDistance = totalDistance / tips.length;
    // Normalizar: más pequeño = más cerrada
    return Math.max(0, Math.min(1, 1 - avgDistance * 2));
  }

  private calculateHandOpenness(landmarks: Array<{x: number, y: number, z: number}>): number {
    // Distancia promedio entre puntas de dedos y muñeca
    const wrist = landmarks[GestureRecognizer.WRIST];
    const mcp = [5, 9, 13, 17]; // Metacarpofalángicas (sin pulgar)
    
    let spread = 0;
    mcp.forEach(idx => {
      const joint = landmarks[idx];
      const dx = joint.x - wrist.x;
      const dy = joint.y - wrist.y;
      spread += Math.sqrt(dx * dx + dy * dy);
    });
    
    // Normalizar
    return Math.min(1, spread / mcp.length * 3);
  }

  // ═════════════════════════════════════════════════════════════════════════════
  // UTILIDADES
  // ═════════════════════════════════════════════════════════════════════════════
  
  getRecentGestures(duration: number = 1000): MedicalGesture[] {
    const now = Date.now();
    return this.gestureHistory
      .filter(g => now - g.time < duration)
      .map(g => g.gesture);
  }

  isGestureActive(gesture: MedicalGesture, duration: number = 500): boolean {
    return this.gestureHistory.some(
      g => g.gesture === gesture && Date.now() - g.time < duration
    );
  }

  reset(): void {
    this.gestureHistory = [];
    this.lastGestureTime = 0;
    this.isPinching = false;
    this.pinchStartTime = 0;
    this.auscultationHoldStart = 0;
    this.emergencyHoldStart = 0;
    this.auscultationLocked = false;
    this.emergencyLocked = false;
    this.hasHoverTarget = false;
  }

  /**
   * Progreso 0..1 del hold activo para auscultación. Null si no hay hold
   * en curso. Lo consume el HUD para mostrar una barra durante el sostén.
   */
  getAuscultationHoldProgress(): number | null {
    if (this.auscultationHoldStart === 0) return null;
    const elapsed = Date.now() - this.auscultationHoldStart;
    return Math.min(1, elapsed / GestureRecognizer.AUSCULTATION_HOLD_MS);
  }

  /**
   * Progreso 0..1 del hold activo para emergencia. Null si no hay hold.
   * Emergencia toggle-ea el layout, así que el feedback visual es clave
   * para que el alumno sepa que "algo se está por activar".
   */
  getEmergencyHoldProgress(): number | null {
    if (this.emergencyHoldStart === 0) return null;
    const elapsed = Date.now() - this.emergencyHoldStart;
    return Math.min(1, elapsed / GestureRecognizer.EMERGENCY_HOLD_MS);
  }

  setConfig(config: Partial<GestureConfig>): void {
    this.config = { ...this.config, ...config };
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
// GESTURE ACTIONS
// ═══════════════════════════════════════════════════════════════════════════════

export interface GestureAction {
  gesture: MedicalGesture;
  action: (context: GestureContext) => void;
  condition?: (context: GestureContext) => boolean;
}

export interface GestureContext {
  handState: HandState;
  otherHand?: HandState;
  hoveredNodeId: string | null;
  selectedNodeId: string | null;
  gestureData?: Record<string, unknown>;
}

export class GestureActionMap {
  private actions: Map<MedicalGesture, GestureAction[]> = new Map();

  register(action: GestureAction): void {
    if (!this.actions.has(action.gesture)) {
      this.actions.set(action.gesture, []);
    }
    this.actions.get(action.gesture)!.push(action);
  }

  execute(gesture: MedicalGesture, context: GestureContext): void {
    const actions = this.actions.get(gesture);
    if (!actions) return;

    actions.forEach(action => {
      if (!action.condition || action.condition(context)) {
        action.action(context);
      }
    });
  }
}

export default GestureRecognizer;
